
var formValidation = require('base/components/formValidation');
var clientSideValidation = require('../components/clientSideValidation');
var tray = require('../components/tray');
var floatingLabel = require('../components/floatLabels');
var formFields = require('../formFields/formFields');
var gtm = require('googletagmanager/tagManager');
let activeFormType = null;

const IBC_SEARCH_NAME = 'ibcsearch-name';
const IBC_SEARCH_PHONE = 'ibcsearch-phone';
const IBC_SEARCH_POSTAL_CODE = 'ibcsearch-postalcode';
const DISABLE_SEARCH_ON_INVALID_FIELD = $('input[name="disableSearchOnInvalidField"]').val() === 'true';

/**
 * Determine whether a phone value contains invalid characters.
 * @param {string} phoneValue - raw phone field value
 * @returns {boolean} true when the value contains characters outside the allowed phone set
 */
function hasInvalidPhoneCharacters(phoneValue) {
    var allowedCharsOnly = /^[0-9()+\s.-]+$/;
    var normalizedValue = String(phoneValue || '').trim();

    if (!normalizedValue) {
        return false;
    }

    return !allowedCharsOnly.test(normalizedValue);
}

/**
 * Resets a select to a single default option.
 * @param {jQuery} $select - Select element to reset
 * @param {string} defaultLabel - Label for the default option
 */
function resetAddressSearchSelect($select, defaultLabel) {
    var $defaultOption = $(document.createElement('option'));
    $defaultOption.val('').text(defaultLabel || 'Select One').prop('selected', true);
    $select.empty();
    $select.append($defaultOption);
    $select.prop('selectedIndex', 0).val('');
}

/**
 * Normalizes API payload lists into plain arrays.
 * @param {*} listValue - Any list-like value
 * @returns {Array} Normalized array
 */
function normalizeResponseList(listValue) {
    if (!listValue) {
        return [];
    }

    if (Array.isArray(listValue)) {
        return listValue;
    }

    if (typeof listValue.toArray === 'function') {
        try {
            return listValue.toArray();
        } catch (e) {
            // no-op
        }
    }

    if (typeof listValue.length === 'number' && typeof listValue !== 'string') {
        try {
            return Array.prototype.slice.call(listValue);
        } catch (e) {
            // no-op
        }
    }

    if (typeof listValue === 'object') {
        var numericKeys = Object.keys(listValue).filter(function (key) {
            return /^\d+$/.test(key);
        });

        if (numericKeys.length) {
            numericKeys.sort(function (a, b) {
                return parseInt(a, 10) - parseInt(b, 10);
            });

            return numericKeys.map(function (key) {
                return listValue[key];
            });
        }
    }

    return [];
}

/**
 * Extracts a displayable city/town value from mixed payload item shapes.
 * @param {*} town - Raw town item
 * @returns {string} Normalized town value
 */
function getTownValue(town) {
    if (typeof town === 'string') {
        return town.trim();
    }

    if (town && typeof town === 'object') {
        return (town.name || town.town || town.displayValue || town.value || '').trim();
    }

    return '';
}

/**
 * Extracts a displayable region value from mixed payload item shapes.
 * @param {*} region - Raw region item
 * @returns {string} Normalized region value
 */
function getRegionValue(region) {
    if (typeof region === 'string') {
        return region.trim();
    }

    if (region && typeof region === 'object') {
        return (region.name || region.region || region.displayValue || region.value || '').trim();
    }

    return '';
}

/**
 * Indicates whether the form has an address2 select (PH flow).
 * @param {jQuery} $form - IBC search form
 * @returns {boolean} True when address2 select exists
 */
function hasAddressSearchAddress2($form) {
    return $form.find('.ibc-address2-select').length > 0;
}

/**
 * Toggles select read-only-like state and preserves original required setting.
 * @param {jQuery} $select - Select element
 * @param {boolean} shouldDisable - Disable state
 */
function setAddressSearchSelectReadOnly($select, shouldDisable) {
    if (!$select.length) {
        return;
    }

    if (typeof $select.data('was-required') === 'undefined') {
        $select.data('was-required', $select.is('[required]'));
    }

    $select.prop('disabled', shouldDisable);
    $select.attr('aria-disabled', shouldDisable ? 'true' : 'false');

    if (shouldDisable) {
        $select.removeAttr('required');
        $select.removeClass('is-invalid');
        $select.closest('.custom-select').removeClass('is-invalid');
        $select.closest('.form-group').removeClass('is-invalid');
        return;
    }

    if ($select.data('was-required')) {
        $select.attr('required', true);
    }
}

/**
 * Applies HK country-specific behavior: MO disables state/city selectors.
 * @param {jQuery} $form - IBC search form
 */
function applyCountrySpecificAddressMode($form) {
    var $countrySelect = $form.find('.ibc-country-select');
    var $stateSelect = $form.find('.ibc-state-select');
    var $citySelect = $form.find('.ibc-city-select');

    if (!$countrySelect.length || !$stateSelect.length || !$citySelect.length) {
        return;
    }

    var isMacao = ($countrySelect.val() || '').toUpperCase() === 'MO';

    if (isMacao) {
        resetAddressSearchSelect($stateSelect, $stateSelect.data('default-label'));
        resetAddressSearchSelect($citySelect, $citySelect.data('default-label'));
        setAddressSearchSelectReadOnly($stateSelect, true);
        setAddressSearchSelectReadOnly($citySelect, true);
    } else {
        setAddressSearchSelectReadOnly($stateSelect, false);
        setAddressSearchSelectReadOnly($citySelect, false);
    }

    formFields.updateSelect();
}

/**
 * Loads city options by selected province.
 * @param {jQuery} $form - IBC search form
 */
function loadAddressSearchCities($form) {
    var $countrySelect = $form.find('.ibc-country-select');
    var $stateSelect = $form.find('.ibc-state-select');
    var $regionInput = $form.find('.ibc-region-input');
    var $citySelect = $form.find('.ibc-city-select');
    var $locationField = $stateSelect.length ? $stateSelect : $regionInput;
    var cityUrl = $locationField.data('get-city-url');
    var state = $locationField.val();
    var country = $countrySelect.val() || '';

    if (!$locationField.length || !$citySelect.length) {
        return;
    }

    if (!state || !cityUrl) {
        return;
    }

    $.getJSON(cityUrl, { state: state, subsidiaryCode: country }).done(function (response) {
        var towns = normalizeResponseList(response && (response.towns || response.regions));
        if (country === 'MO') {
            $form.find('#ibcSearchCityId').val(towns[0] ? towns[0].id : '');
        } else {
            resetAddressSearchSelect($citySelect, $citySelect.data('default-label'));
            towns.forEach(function (town) {
                var townValue = getTownValue(town);
                if (!townValue) {
                    return;
                }

                var $option = $(document.createElement('option')).val(townValue).text(townValue);
                if (town.id) {
                    $option.attr('data-external-id', town.id);
                }
                $citySelect.append($option);
            });
        }

        formFields.updateSelect();
    });
}

/**
 * load address search states
 * @param {jQuery} $form - IBC search form
 */
function loadAddressSearchStates($form) {
    var $countrySelect = $form.find('.ibc-country-select');
    var country = $countrySelect.find('option:selected').val();
    var url = $countrySelect.data('get-state-url') + (country ? '?subsidiaryCode=' + country : '');
    $.ajax({
        url: url,
        type: 'get',
        dataType: 'json',
        success: function (res) {
            if (!res.error) {
                var $stateSelect = $form.find('.ibc-state-select');
                $stateSelect.empty();
                $stateSelect.append('<option id="" value="">' + res.defaultValue + '</option>');
                var states = normalizeResponseList(res.regions);
                var stateValue;
                if (country === 'MO') {
                    stateValue = getRegionValue(states[0]);
                    $stateSelect.append('<option id="' + stateValue + '" value="' + stateValue + '" selected></option>');
                    loadAddressSearchCities($form)
                } else {
                    states.forEach(function (state) {
                        stateValue = getRegionValue(state);
                        $stateSelect.append('<option id="' + stateValue + '" value="' + stateValue + '">' + stateValue + '</option>');
                    });
                }
                applyCountrySpecificAddressMode($form);
            }
        }
    });
}

/**
 * Loads Region suggestions for address-based IBC search.
 * @param {jQuery} $input - Region input
 * @returns {void}
 */
function loadAddressSearchRegions($input) {
    var $form = $input.closest('form.ibcsearch-postalcode');
    var $suggestions = $form.find('.ibc-region-suggestions');
    var query = ($input.val() || '').trim();
    var url = $input.data('get-region-url');
    var minCharacters = parseInt($input.data('autocomplete-min-chars'), 10) || 1;

    $suggestions.empty();

    if (!url || query.length < minCharacters) {
        return;
    }

    if ($input.data('region-xhr')) {
        $input.data('region-xhr').abort();
    }

    $input.data('region-xhr', $.getJSON(url, { q: query }).done(function (response) {
        if (response.error) {
            return;
        }

        normalizeResponseList(response.suggestions).forEach(function (region) {
            var value = getRegionValue(region);
            if (!value) {
                return;
            }

            var $suggestion = $(document.createElement('a'))
                .attr('href', '#')
                .addClass('search-suggestion')
                .attr('data-region', value)
                .text(value);
            var $brElem = $(document.createElement('br'));
            $suggestions.append($suggestion);
            $suggestions.append($brElem);
        });
    }));
}

/**
 * Loads Town/City suggestions for address-based IBC search, scoped to the selected region.
 * @param {jQuery} $input - City/town input
 * @returns {void}
 */
function loadAddressSearchTowns($input) {
    var $form = $input.closest('form.ibcsearch-postalcode');
    var $suggestions = $form.find('.ibc-city-suggestions');
    var region = ($form.find('.ibc-region-input').val() || '').trim();
    var query = ($input.val() || '').trim();
    var url = $input.data('get-city-url');
    var minCharacters = parseInt($input.data('autocomplete-min-chars'), 10) || 0;

    $suggestions.empty();

    if (!url || !region || query.length < minCharacters || (!query && minCharacters === 0)) {
        return;
    }

    if ($input.data('town-xhr')) {
        $input.data('town-xhr').abort();
    }

    $input.data('town-xhr', $.getJSON(url, { region: region, q: query }).done(function (response) {
        if (response.error) {
            return;
        }

        normalizeResponseList(response.suggestions).forEach(function (town) {
            var value = getTownValue(town);
            if (!value) {
                return;
            }

            var $suggestion = $(document.createElement('a'))
                .attr('href', '#')
                .addClass('search-suggestion')
                .attr('data-town', value)
                .text(value);

            if (town.id) {
                $suggestion.attr('data-town-id', town.id);
            }

            var $brElem = $(document.createElement('br'));
            $suggestions.append($suggestion);
            $suggestions.append($brElem);
        });
    }));
}

/**
 * Loads address2 options by selected province/city.
 * @param {jQuery} $form - IBC search form
 */
function loadAddressSearchAddress2($form) {
    var $stateSelect = $form.find('.ibc-state-select');
    var $citySelect = $form.find('.ibc-city-select');
    var $address2Select = $form.find('.ibc-address2-select');
    var addressUrl = $citySelect.data('get-address2-url');
    var state = $stateSelect.val();
    var city = $citySelect.val();

    if (!$stateSelect.length || !$citySelect.length) {
        return;
    }

    resetAddressSearchSelect($address2Select, $address2Select.data('default-label'));

    if (!city || !addressUrl || !state) {
        return;
    }

    var renderAddress2 = function (response) {
        var addressList = normalizeResponseList(response && response.addressList);

        if (!addressList.length) {
            var towns = normalizeResponseList(response && (response.towns || response.regions));
            addressList = towns.map(function (item) {
                var townValue = getTownValue(item);
                if (!townValue) {
                    return null;
                }

                return {
                    address: townValue,
                    postalCode: item && (item.postalCode || item.postal_code) ? (item.postalCode || item.postal_code) : ''
                };
            }).filter(function (item) {
                return item;
            });
        }

        addressList.forEach(function (item) {
            var value = item && item.address ? item.address : '';
            if (!value) {
                return;
            }

            var $option = $(document.createElement('option'))
                .val(value)
                .text(value)
                .attr('data-post-code', item.postalCode || '');

            $address2Select.append($option);
        });

        $address2Select.prop('selectedIndex', 0).val('');

        formFields.updateSelect();
    };

    $.getJSON(addressUrl, { state: state, town: city }).done(function (response) {
        renderAddress2(response);
    });
}

/**
 * Smoothly scrolls the page to the top.
 *
 * @function
 * @param {number|string} [speed='slow'] - The speed of the animation.
 * Accepts a number (in milliseconds) or jQuery speed keywords like 'slow' or 'fast'.
 */
function scrollToTop(speed = 'slow') {
    $('html, body').animate({ scrollTop: 0 }, speed);
}

/**
 * Captures selected address-search values before results replace the form.
 * @param {jQuery} $form - Submitted postal search form
 * @returns {Object|null} Selected address-search values or null for other forms
 */
function getAddressSearchSelections($form) {
    if (!$form.hasClass('ibcsearch-postalcode--address-selection')) {
        return null;
    }
    var $stateSelect = $form.find('.ibc-state-select');
    var $citySelect = $form.find('.ibc-city-select');
    var $cityInput = $form.find('.ibc-city-input');

    return {
        region: $stateSelect.length ? ($stateSelect.val() || '') : ($form.find('.ibc-region-input').val() || ''),
        townId: $citySelect.length ? ($citySelect.val() || '') : ($cityInput.val() || ''),
        townName: $citySelect.length ? ($citySelect.find('option:selected').text() || '') : ($cityInput.val() || ''),
        radius: $form.find('.ibc-radius-select').val() || ''
    };
}

/**
 * Restores selected address-search values after results render a new form.
 * @param {jQuery} $form - Rendered postal search form
 * @param {Object|null} selections - Address-search values from the submitted form
 * @returns {void}
 */
function restoreAddressSearchSelections($form, selections) {
    if (!selections || !$form.hasClass('ibcsearch-postalcode--address-selection')) {
        return;
    }

    var $citySelect = $form.find('.ibc-city-select');
    var $cityInput = $form.find('.ibc-city-input');

    $form.find('.ibc-region-input').val(selections.region);
    if (selections.townId) {
        if ($citySelect.length) {
            // Try to find an existing option by value, text, or external id. If found, select it.
            var $existingOption = $citySelect.find('option').filter(function () {
                var $opt = $(this);
                return ($opt.data('external-id') === selections.townId) || ($opt.val() === selections.townId) || ($opt.text() === selections.townName);
            }).first();

            if ($existingOption && $existingOption.length) {
                $existingOption.prop('selected', true);
            } else {
                $citySelect.append($(document.createElement('option'))
                    .val(selections.townId)
                    .text(selections.townName)
                    .prop('selected', true));
            }
            setAddressSearchSelectReadOnly($citySelect, false);
        } else if ($cityInput.length) {
            $cityInput.val(selections.townName).prop('disabled', false).data('town-confirmed', true);
        }
    }
    $form.find('.ibc-radius-select').val(selections.radius);
    $form.find('[data-search-toggle="true"]')
        .prop('disabled', !(selections.region && selections.townId && selections.radius))
        .toggleClass('ibc-submit-btn-disabled', !(selections.region && selections.townId && selections.radius));
}

/**
 * Renders the results of the search and updates the map
 * @param {Object} data - Response from the server
 * @param {string|null} formType - The identifier used for form type (e.g., 'ibcsearch-name').
 * @param {jQuery} $submittedForm - Form that initiated the search
 */
function updateStoresResults(data, formType, $submittedForm) {
    var $resultsDiv = $('.ibcsearch-page');
    var hasResults;
    var token = null;
    var addressSelections = getAddressSearchSelections($submittedForm);

    // store current token to re-apply after results are rendered
    if (formType === IBC_SEARCH_POSTAL_CODE) {
        token = $('form.' + IBC_SEARCH_POSTAL_CODE + ' .g-recaptcha-token').val();
    }
    if ('stores' in data && data.stores) {
        hasResults = true;
    } else {
        hasResults = false;
    }
    if (!hasResults) {
        $('.store-locator-no-results').show();
    } else {
        $('.store-locator-no-results').hide();
    }

    $resultsDiv.empty()
        .data('has-results', hasResults)
        .data('radius', hasResults && data.stores.radius ? data.stores.radius : 0)
        .data('search-key', hasResults && data.stores.searchKey ? data.stores.searchKey : '');

    if (data && data.stores && data.stores.storesResultsHtml) {
        $('.load-more-btn').remove();
        let visibleCount = 6;
        let $ibcGridTemplate = $(data.stores.storesResultsHtml);
        let availableCards = $($ibcGridTemplate).find('.ibc-card-container').length;
        $resultsDiv.append(data.stores.storesResultsHtml);
        $resultsDiv.find('form.ibcsearch-postalcode').each(function () {
            var $form = $(this);
            applyCountrySpecificAddressMode($form);
            restoreAddressSearchSelections($form, addressSelections);
        });
        if (token) {
            $resultsDiv.find('.g-recaptcha-token').val(token);
        }
        formFields.updateSelect();
        floatingLabel();
        scrollToTop();
        if (availableCards > visibleCount) {
            const loadButtonText = $('.ibc-grid').attr('data-load-button');
            const $btn = $('<button id="loadMoreBtn" class="load-more-btn d-block">' + loadButtonText + '</button>');
            $('.ibcsearch-page .load-more-btn-wrapper').append($btn);
        }
    }
}


/**
 * Validates form fields for name or phone-based search forms.
 *
 * @param {jQuery} $form - The jQuery-wrapped form element to validate.
 * @param {string} validateFormFlag - The type of form being validated, e.g., 'ibcsearch-name' or 'ibcsearch-phone'.
 */
function validateField($form, validateFormFlag) {
    clientSideValidation.checkMandatoryField($form);
    if (validateFormFlag === 'ibcsearch-name') {
        var $firstNameField = $form.find('input.js-firstName');
        var $lastNameField = $form.find('input.js-lastName');

        var firstNameLength = parseInt($firstNameField.attr('minLength'), 10);
        var lastNameLength = parseInt($lastNameField.attr('minLength'), 10);
        var firstNamePattern = $firstNameField.attr('pattern');
        var lastNamePattern = $lastNameField.attr('pattern');
        var isValidFirstName = $firstNameField.length ? clientSideValidation.nameValidation($firstNameField.val().trim(), firstNameLength, firstNamePattern) : true;
        var isValidLastName = clientSideValidation.nameValidation($lastNameField.val().trim(), lastNameLength, lastNamePattern);

        $firstNameField.addClass('is-invalid');
        var validationMessageFN = $firstNameField.data('value-error');
        $firstNameField.closest('.form-group').find('.invalid-feedback').text(validationMessageFN);

        $lastNameField.addClass('is-invalid');
        var validationMessageLN = $lastNameField.data('value-error');
        $lastNameField.closest('.form-group').find('.invalid-feedback').text(validationMessageLN);

        if (isValidFirstName) {
            $firstNameField.removeClass('is-invalid');
        }
        if (isValidLastName) {
            $lastNameField.removeClass('is-invalid');
        }
    } else if (validateFormFlag === 'ibcsearch-phone') {
        var $phoneNoField = $form.find('input.js-phone');
        var phoneNoValue = ($phoneNoField.val() || '').trim();
        var phoneDigitCount = phoneNoValue.replace(/\D/g, '').length;
        var phoneMinLength = Number($phoneNoField.attr('minlength'));
        var phoneMaxLength = Number($phoneNoField.attr('maxlength'));
        var pattern = $phoneNoField.attr('pattern');
        var isValidPhone;
        var hasPhoneLengthError = false;
        var hasPhoneCharacterError = hasInvalidPhoneCharacters(phoneNoValue);
        var fullPhoneNumber = $form.find('input#fullPhoneNumber');
        var phoneCode = $form.find('select#phoneCode').val();
        if (fullPhoneNumber.length && phoneCode) {
            phoneNoValue = phoneCode + phoneNoValue;
            phoneDigitCount = phoneNoValue.replace(/\D/g, '').length;
            pattern = fullPhoneNumber.attr('pattern');
            fullPhoneNumber.val(phoneNoValue);
        }

        if (pattern) {
            isValidPhone = clientSideValidation.phoneNoValidation(phoneNoValue, pattern);
        } else {
            isValidPhone = clientSideValidation.phoneNoValidation(phoneNoValue);
        }

        hasPhoneLengthError = phoneDigitCount > 0
            && ((phoneMinLength && phoneDigitCount < phoneMinLength)
                || (phoneMaxLength && phoneDigitCount > phoneMaxLength));

        $phoneNoField.addClass('is-invalid');
        var validationMessagePhone = $phoneNoField.data('value-error');

        if (hasPhoneLengthError && !hasPhoneCharacterError) {
            validationMessagePhone = $phoneNoField.data('range-error');
        }
        $phoneNoField.closest('.form-group').find('.invalid-feedback').text(validationMessagePhone);

        if (isValidPhone) {
            $phoneNoField.removeClass('is-invalid');
        }
    }
}

/**
 * Determines whether the postal form requires address selections.
 * @param {jQuery} $form - Postal search form
 * @returns {boolean} True when address selections are required
 */
function requiresAddressSelections($form) {
    return $form.hasClass('ibcsearch-postalcode--address-selection');
}

/**
 * Ensures postal search has all required location selectors chosen.
 * @param {jQuery} $form - Postal search form
 * @returns {boolean} True only when region/state, town and radius are selected
 */
function hasRequiredPostalSelections($form) {
    var $regionInput = $form.find('.ibc-region-input');
    var $stateSelect = $form.find('.ibc-state-select');
    var $citySelect = $form.find('.ibc-city-select');
    var $cityInput = $form.find('.ibc-city-input');
    var $radiusSelect = $form.find('.ibc-radius-select').first();

    var regionOrStateSelected = true;
    if ($regionInput.length) {
        regionOrStateSelected = Boolean(($regionInput.val() || '').trim());
    } else if ($stateSelect.length) {
        regionOrStateSelected = Boolean(($stateSelect.val() || '').trim());
    }

    var townSelected = true;
    if ($citySelect.length) {
        townSelected = Boolean(($citySelect.val() || '').trim());
    } else if ($cityInput.length) {
        townSelected = Boolean(($cityInput.val() || '').trim()) && $cityInput.data('town-confirmed') === true;
    }

    var radiusSelected = !$radiusSelect.length || Boolean(($radiusSelect.val() || '').trim());

    return regionOrStateSelected && townSelected && radiusSelected;
}

/**
 * toggleSearchButtonState
 * @param {Form} $form - baseline form for processing
 * @param {boolean} shouldValidate - whether to run validation before toggling search button state
 * @returns {boolean} true if form was handled, false if feature is disabled or form is already invalid or unsupported
 */
function toggleSearchButtonState($form, shouldValidate = true) {
    var requiresAddressSelection = $form.hasClass(IBC_SEARCH_POSTAL_CODE) && requiresAddressSelections($form);
    if (!DISABLE_SEARCH_ON_INVALID_FIELD && !requiresAddressSelection) {
        return false;
    }

    var setSearchButtonState = function (form, formClass, validatorFunction, additionalValidityCheck) {
        var isValid = form.find('input.is-invalid, select.is-invalid, .form-group.is-invalid').length === 0;
        var $citySelect = form.find('.ibc-city-select');
        if ($citySelect.length && $citySelect.find('option:selected').text() === $citySelect.data('default-label')) {
            isValid = false;
        }
        var $searchButton = form.find('[data-search-toggle="true"]');
        // address-selection validity (e.g. town-confirmed) isn't reflected via is-invalid, so it must always be re-checked
        if (isValid && !$searchButton.prop('disabled') && !requiresAddressSelections(form)) {
            return false;
        }
        if (form.hasClass(formClass)) {
            if (shouldValidate) {
                validatorFunction();
            }
            isValid = form.find('input.is-invalid, select.is-invalid, .form-group.is-invalid').length === 0;
            if (typeof additionalValidityCheck === 'function') {
                if (requiresAddressSelections(form)) {
                    isValid = additionalValidityCheck(form);
                } else {
                    isValid = isValid && additionalValidityCheck(form);
                }
            }
            $searchButton.prop('disabled', !isValid);
            $searchButton.toggleClass('ibc-submit-btn-disabled', !isValid);
            return true;
        }
        return false;
    }

    return (
        // CASE 1: Name form
        DISABLE_SEARCH_ON_INVALID_FIELD && setSearchButtonState($form, IBC_SEARCH_NAME, () => validateField($form, IBC_SEARCH_NAME)) ||
        // CASE 2: Phone form
        DISABLE_SEARCH_ON_INVALID_FIELD && setSearchButtonState($form, IBC_SEARCH_PHONE, () => validateField($form, IBC_SEARCH_PHONE)) ||
        // CASE 3: Postal code form, prevent refocus as it will block entry in other forms if postal code form is invalid
        setSearchButtonState($form, IBC_SEARCH_POSTAL_CODE, () => {
            if (DISABLE_SEARCH_ON_INVALID_FIELD) {
                return clientSideValidation.checkMandatoryField($form, false);
            }
            return true;
        }, function (form) {
            return !requiresAddressSelections(form) || hasRequiredPostalSelections(form);
        })
    );
}

/**
* Handles the AJAX form submission for different IBC search forms.
*
* @param {jQuery} $form - The jQuery-wrapped form element.
* @param {string} formType - The identifier used for form type (e.g., 'ibcsearch-name').
* @param {Function} [onSuccess] - Optional callback function to execute on success.
*/
function submitSearchForm($form, formType, onSuccess) {
    const url = $form.attr('action');
    const triggerBase = `form.${formType}`;

    $.customSpinner().start();
    $(triggerBase).trigger(`${formType}:submit`);

    $.ajax({
        url: url,
        type: 'post',
        dataType: 'json',
        data: $form.serialize(),
        success: function (data) {
            $.customSpinner().stop();
            if (!data.success) {
                formValidation($form, data);
                $(triggerBase).trigger(`${formType}:error`, data);
            } else {
                $(triggerBase).trigger(`${formType}:success`, data);
                if (typeof onSuccess === 'function') onSuccess(data);
                updateStoresResults(data, formType, $form);
                gtm.connectConsultant(data, formType, $form);
                tray.InitTrayModal();
            }
            if (data.phoneCode) {
                $('.ibcsearch-page #phoneCode').val(data.phoneCode).trigger('change');
            }
        },
        error: function (data) {
            $.customSpinner().stop();
            if (data.responseJSON && data.responseJSON.redirectUrl) {
                window.location.href = data.responseJSON.redirectUrl;
            } else {
                $(triggerBase).trigger(`${formType}:error`, data);
            }
        }
    });
}

module.exports = {
    addressSearchDropdowns: function () {
        $('body')
            .off('change.ibcAddressSearchCountry', 'form.ibcsearch-postalcode .ibc-country-select')
            .on('change.ibcAddressSearchCountry', 'form.ibcsearch-postalcode .ibc-country-select', function () {
                var $form = $(this).closest('form.ibcsearch-postalcode');
                loadAddressSearchStates($form);
                toggleSearchButtonState($form, true);
            });

        $('body')
            .off('change.ibcAddressSearchState', 'form.ibcsearch-postalcode .ibc-state-select')
            .on('change.ibcAddressSearchState', 'form.ibcsearch-postalcode .ibc-state-select', function () {
                var $form = $(this).closest('form.ibcsearch-postalcode');
                loadAddressSearchCities($form);
                toggleSearchButtonState($form, true);
            });

        $('body')
            .off('input.ibcAddressSearchRegion', 'form.ibcsearch-postalcode .ibc-region-input')
            .on('input.ibcAddressSearchRegion', 'form.ibcsearch-postalcode .ibc-region-input', function () {
                var $input = $(this);
                var $form = $input.closest('form.ibcsearch-postalcode');
                var $citySelect = $form.find('.ibc-city-select');
                var $cityInput = $form.find('.ibc-city-input');

                resetAddressSearchSelect($citySelect, $citySelect.data('default-label'));
                setAddressSearchSelectReadOnly($citySelect, true);
                if ($cityInput.length) {
                    $cityInput.val('').prop('disabled', true).removeClass('is-invalid').data('town-confirmed', false);
                    $form.find('.ibc-city-suggestions').empty();
                }
                window.clearTimeout($input.data('region-suggestion-timeout'));
                $input.data('region-suggestion-timeout', window.setTimeout(function () {
                    loadAddressSearchRegions($input);
                    loadAddressSearchCities($form);
                    if ($cityInput.length) {
                        $cityInput.prop('disabled', !($input.val() || '').trim());
                    }
                }, 300));
                var $searchButton = $('body').find('[data-search-toggle="true"]');
                $searchButton.prop('disabled', true);
                $searchButton.toggleClass('ibc-submit-btn-disabled', true);
            });

        $('body')
            .off('click.ibcAddressSearchRegion', 'form.ibcsearch-postalcode .ibc-region-suggestions .search-suggestion')
            .on('click.ibcAddressSearchRegion', 'form.ibcsearch-postalcode .ibc-region-suggestions .search-suggestion', function (event) {
                event.preventDefault();
                var $suggestion = $(this);
                var $form = $suggestion.closest('form.ibcsearch-postalcode');
                var $cityInput = $form.find('.ibc-city-input');

                $form.find('.ibc-region-input').val($suggestion.data('region')).removeClass('is-invalid');
                $form.find('.ibc-region-suggestions').empty();
                loadAddressSearchCities($form);
                if ($cityInput.length) {
                    $cityInput.val('').prop('disabled', false).removeClass('is-invalid').data('town-confirmed', false);
                    $form.find('.ibc-city-suggestions').empty();
                }
                toggleSearchButtonState($form, false);
            });

        $('body')
            .off('input.ibcAddressSearchCity', 'form.ibcsearch-postalcode .ibc-city-input')
            .on('input.ibcAddressSearchCity', 'form.ibcsearch-postalcode .ibc-city-input', function () {
                var $input = $(this);
                var $form = $input.closest('form.ibcsearch-postalcode');
                var $searchButton = $form.find('[data-search-toggle="true"]');

                $input.removeClass('is-invalid').data('town-confirmed', false);
                $searchButton.prop('disabled', true).toggleClass('ibc-submit-btn-disabled', true);
                window.clearTimeout($input.data('town-suggestion-timeout'));
                $input.data('town-suggestion-timeout', window.setTimeout(function () {
                    loadAddressSearchTowns($input);
                }, 300));
                toggleSearchButtonState($form, false);
            });

        $('body')
            .off('click.ibcAddressSearchCity', 'form.ibcsearch-postalcode .ibc-city-suggestions .search-suggestion')
            .on('click.ibcAddressSearchCity', 'form.ibcsearch-postalcode .ibc-city-suggestions .search-suggestion', function (event) {
                event.preventDefault();
                var $suggestion = $(this);
                var $form = $suggestion.closest('form.ibcsearch-postalcode');

                $form.find('.ibc-city-input').val($suggestion.data('town')).removeClass('is-invalid').data('town-confirmed', true);
                $form.find('.ibc-city-id').val($suggestion.data('town-id'));
                $form.find('.ibc-city-suggestions').empty();
                toggleSearchButtonState($form, false);
            });

        $('body')
            .off('change.ibcAddressSearchCity', 'form.ibcsearch-postalcode .ibc-city-select')
            .on('change.ibcAddressSearchCity', 'form.ibcsearch-postalcode .ibc-city-select', function () {
                var $form = $(this).closest('form.ibcsearch-postalcode');

                if (hasAddressSearchAddress2($form)) {
                    loadAddressSearchAddress2($form);
                } else {
                    var $hiddenCityId = $form.find('#ibcSearchCityId');
                    if ($hiddenCityId.length) {
                        var selectedCityId = $(this).find('option:selected').data('external-id') || '';
                        $hiddenCityId.val(selectedCityId || '');
                    }
                }

                toggleSearchButtonState($form, true);
            });

        $('body')
            .off('change.ibcAddressSearchAddress2', 'form.ibcsearch-postalcode .ibc-address2-select')
            .on('change.ibcAddressSearchAddress2', 'form.ibcsearch-postalcode .ibc-address2-select', function () {
                var $form = $(this).closest('form.ibcsearch-postalcode');
                var $hiddenPostalCode = $form.find('.ibc-hidden-postal');
                if ($hiddenPostalCode.length) {
                    var selectedPostal = $(this).find('option:selected').data('post-code') || '';
                    $hiddenPostalCode.val(selectedPostal);
                }
                toggleSearchButtonState($form, true);
            });

        $('body')
            .off('change.ibcAddressSearchRadius', 'form.ibcsearch-postalcode .ibc-radius-select, form.ibcsearch-postalcode #radius')
            .on('change.ibcAddressSearchRadius', 'form.ibcsearch-postalcode .ibc-radius-select, form.ibcsearch-postalcode #radius', function () {
                toggleSearchButtonState($(this).closest('form.ibcsearch-postalcode'), true);
            });

        // HK specific initialization
        if ($('form.ibcsearch-postalcode select.ibc-country-select').length > 0) {
            $('form.ibcsearch-postalcode select.ibc-country-select').val('HK').trigger('change');
        }
    },
    postalCodeSearch: function () {
        $('body').on('submit', 'form.ibcsearch-postalcode', function (e) {
            var $form = $(this);
            var gRecaptchaInput = $form.find('.g-recaptcha-token');
            var gRecaptchaButton = $form.find('.g-recaptcha');
            e.preventDefault();

            var isValid = clientSideValidation.checkMandatoryField($form);
            if (typeof isValid === 'undefined') {
                isValid = !$form.find('input.is-invalid, select.is-invalid, .form-group.is-invalid').length;
            }

            if (isValid) {
                activeFormType = 'Zip Code';
                if (!gRecaptchaInput.val() && gRecaptchaButton.length > 0) {
                    var siteKey = (gRecaptchaButton.attr('data-sitekey') || '').trim();
                    var hasRecaptchaRuntime = typeof window.grecaptcha !== 'undefined';

                    if (siteKey && hasRecaptchaRuntime) {
                        gRecaptchaButton.trigger('click');
                        return false;
                    }
                }
                submitSearchForm($form, IBC_SEARCH_POSTAL_CODE);
            } else {
                toggleSearchButtonState($form, false);
            }
            return false;
        });
    },
    nameSearch: function () {
        $('body').on('submit', 'form.ibcsearch-name', function (e) {
            var $form = $(this);
            e.preventDefault();
            var validateFormFlag = 'ibcsearch-name';
            validateField($form, validateFormFlag);
            if (!$form.find('input.is-invalid').length) {
                activeFormType = 'Name';
                submitSearchForm($form, 'ibcsearch-name', () => {
                    $('.tray-popup-close').trigger('click');
                });
            } else {
                $form.find('input.is-invalid').first().focus();
                toggleSearchButtonState($form, false);
            }
            return false;
        });
    },
    phoneSearch: function () {
        $('body').on('submit', 'form.ibcsearch-phone', function (e) {
            var $form = $(this);
            e.preventDefault();
            var validateFormFlag = 'ibcsearch-phone';
            validateField($form, validateFormFlag);
            if (!$form.find('input.is-invalid').length) {
                activeFormType = 'Phone Number';
                submitSearchForm($form, 'ibcsearch-phone', () => {
                    $('.tray-popup-close').trigger('click');
                });
            } else {
                $form.find('input.is-invalid').first().focus();
                toggleSearchButtonState($form, false);
            }
            return false;
        });
    },
    clearPhoneField: function () {
        $('body').on('click', '.clear-icon', function (e) {
            e.preventDefault();
            $(this).siblings('.phone-input').val('').focus();
        });
    },
    loadMore: function () {
        $('body').on('click', '.load-more-btn', function () {
            const $ibcGrid = $('.ibc-grid');
            const $hidden = $ibcGrid.find('.ibc-card-container.d-none').slice(0, 6);
            $hidden.each(function () {
                $(this).removeClass('d-none');
            });
            if ($ibcGrid.find('.ibc-card-container.d-none').length === 0) {
                $(this).removeClass('d-block').addClass('d-none');
            }
            gtm.consultantLocatorLoadMore(activeFormType);
        });
    },
    clearSearchForm: function () {
        $('body').on('click', '.name-phone-search-wrapper .nav-link', function () {
            const $clickedTab = $(this);
            const formId = $clickedTab.attr('href');
            const $form = $(formId).find('form');

            $form.find('input[required]').val('');
            $('body').trigger('initFloatLabels');
            clientSideValidation.functions.clearForm($form);
        });
    },
    shareProfile: function () {
        $('body').on('click', '.js-ibc-share-btn', function () {
            var url = $(this).data('url');
            if (navigator.share) {
                navigator.share({
                    title: document.title,
                    text: 'Check this out!',
                    url: url
                })
            }
        });
    },
    checkoutIBCSelection: function () {
        const queryUrl = new URLSearchParams(window.location.search);
        if (queryUrl.get('IBCSelection') === 'manual' && queryUrl.get('redirectURL') === 'checkout') {
            $('.js-ibcselection-manual').remove();
            $('.already-know-text').addClass('mt-0');
        } else {
            $('.js-ibcselection-manual').removeClass('d-none')
        }
    },
    joinTeamSelect: function () {
        $('body').on("click", 'a[data-action="join-team"]', function () {
            const link = $(this);
            const $card = $(link).closest('.ibc-card-container');
            const slotNumber = $card.index() + 1;
            // const $shopLink = $card.find('a[data-action="view-profile"]');

            // var consultantNumber = $shopLink.length ? $shopLink.data("store-id") : null;
            const consultantNumber = $card.data("consultantnumber");
            // var active = $shopLink.length ? $shopLink.data("active") : null;
            const active = $card.data("active");
            gtm.joinTeamSelect(active, consultantNumber, slotNumber);
        });
        $('body').on("click", 'a[data-action="join-team-search-profile"]', function () {
            const $link = $(this);
            const consultantNumber = $link.data('consultant-number');
            const slotNumber = $link.data('slot');
            const active = $link.data('active');
            gtm.joinTeamSelect(active, consultantNumber, slotNumber);
        });
    },
    focusoutFormInputs: function () {
        $('body').on('focusout',
            'form.ibcsearch-name input, form.ibcsearch-name select, '
            + 'form.ibcsearch-phone input, form.ibcsearch-phone select, '
            + 'form.ibcsearch-postalcode input, form.ibcsearch-postalcode select',
            function (e) {
                var $form = $(this).closest('form');
                var $relatedTarget = $(e.relatedTarget);
                var isSameFormField = $relatedTarget.length
                    && $form.find($relatedTarget).length > 0
                    && $relatedTarget.is('input:not([type="submit"]):not([type="button"]), select');

                if (isSameFormField) {
                    return;
                }
                toggleSearchButtonState($form, true);
            });
    }
};
